import { mapFormStateToCreateInput } from '@griply/services';
import { CalendarEventFormFields, ExternalCalendarEvent } from '@griply/types';
import {
  buildEventFormOptimisticEvent,
  doesWindowCoverExternalEvent,
} from '@griply/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createExternalCalendarEvent } from 'shared/services/backend-api';
import { v4 as uuidv4 } from 'uuid';

import { queryKey as eventsQueryKey } from './use-external-calendar-events-query';

type CachedEvents = ExternalCalendarEvent[] | undefined;

export const useCreateExternalCalendarEventMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (fields: CalendarEventFormFields) =>
      createExternalCalendarEvent(mapFormStateToCreateInput(fields)),

    // These callables have no snapshot listener behind them, so without a placeholder the
    // new event stays invisible for the whole create round-trip plus the refetch.
    onMutate: async (fields) => {
      if (!fields.calendar) {
        return null;
      }

      const optimisticEvent: ExternalCalendarEvent = {
        ...buildEventFormOptimisticEvent(fields),
        id: uuidv4(),
        connectionId: fields.calendar.connectionId,
        calendarId: fields.calendar.calendarId,
        pending: true,
      };

      const previous = client.getQueriesData<ExternalCalendarEvent[]>({
        queryKey: [eventsQueryKey],
      });

      previous.forEach(([key, events]) => {
        if (!doesWindowCoverExternalEvent(key, optimisticEvent)) {
          return;
        }
        client.setQueryData<CachedEvents>(key, [
          ...(events ?? []),
          optimisticEvent,
        ]);
      });

      return { previous };
    },

    onError: (_error, _fields, context) => {
      context?.previous.forEach(([key, events]) =>
        client.setQueryData<CachedEvents>(key, events),
      );
    },

    onSettled: async () => {
      await client.invalidateQueries({ queryKey: [eventsQueryKey] });
    },
  });
};
